import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";

// ─── COSTANTI ESTETICHE ───────────────────────────────────────────────────────
const C = {
  bg:       "#0a0a0f",
  surface:  "rgba(255,255,255,0.04)",
  sh:       "rgba(255,255,255,0.07)",
  border:   "rgba(255,255,255,0.10)",
  text:     "#f0ede8",
  sec:      "rgba(240,237,232,0.55)",
  ter:      "rgba(240,237,232,0.28)",
  gold:     "#c9a84c",
  goldBg:   "rgba(201,168,76,0.12)",
  goldBr:   "rgba(201,168,76,0.30)",
  silver:   "#8a9bb0",
  silverBg: "rgba(138,155,176,0.12)",
  silverBr: "rgba(138,155,176,0.30)",
  light:    "#d4c5a9",
  dark:     "#5c4a32",
  lightHl:  "#e8d5a3",
  darkHl:   "#7a6040",
  sel:      "rgba(201,168,76,0.35)",
  move:     "rgba(201,168,76,0.20)",
  capture:  "rgba(220,60,60,0.30)",
  check:    "rgba(220,60,60,0.55)",
};
const F = "'Georgia', 'Times New Roman', serif";
const FM = "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif";

// ─── LOGICA SCACCHI ───────────────────────────────────────────────────────────
const INIT_BOARD = [
  ["bR","bN","bB","bQ","bK","bB","bN","bR"],
  ["bP","bP","bP","bP","bP","bP","bP","bP"],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  ["wP","wP","wP","wP","wP","wP","wP","wP"],
  ["wR","wN","wB","wQ","wK","wB","wN","wR"],
];

const PIECE_UNICODE = {
  wK:"♔", wQ:"♕", wR:"♖", wB:"♗", wN:"♘", wP:"♙",
  bK:"♚", bQ:"♛", bR:"♜", bB:"♝", bN:"♞", bP:"♟",
};

function colorOf(piece) { return piece ? piece[0] : null; }
function typeOf(piece)  { return piece ? piece[1] : null; }

function inBounds(r, c) { return r >= 0 && r < 8 && c >= 0 && c < 8; }

function rawMoves(board, r, c, enPassant) {
  const piece = board[r][c];
  if (!piece) return [];
  const col = colorOf(piece);
  const type = typeOf(piece);
  const moves = [];
  const opp = col === "w" ? "b" : "w";

  const slide = (dr, dc) => {
    let nr = r + dr, nc = c + dc;
    while (inBounds(nr, nc)) {
      if (board[nr][nc]) {
        if (colorOf(board[nr][nc]) === opp) moves.push([nr, nc]);
        break;
      }
      moves.push([nr, nc]);
      nr += dr; nc += dc;
    }
  };

  if (type === "R") { [[-1,0],[1,0],[0,-1],[0,1]].forEach(([dr,dc]) => slide(dr,dc)); }
  if (type === "B") { [[-1,-1],[-1,1],[1,-1],[1,1]].forEach(([dr,dc]) => slide(dr,dc)); }
  if (type === "Q") { [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[-1,1],[1,-1],[1,1]].forEach(([dr,dc]) => slide(dr,dc)); }

  if (type === "N") {
    [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]].forEach(([dr,dc]) => {
      const nr = r+dr, nc = c+dc;
      if (inBounds(nr,nc) && colorOf(board[nr][nc]) !== col) moves.push([nr,nc]);
    });
  }

  if (type === "K") {
    [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]].forEach(([dr,dc]) => {
      const nr = r+dr, nc = c+dc;
      if (inBounds(nr,nc) && colorOf(board[nr][nc]) !== col) moves.push([nr,nc]);
    });
  }

  if (type === "P") {
    const dir = col === "w" ? -1 : 1;
    const startRow = col === "w" ? 6 : 1;
    if (inBounds(r+dir, c) && !board[r+dir][c]) {
      moves.push([r+dir, c]);
      if (r === startRow && !board[r+2*dir][c]) moves.push([r+2*dir, c]);
    }
    [-1,1].forEach(dc => {
      const nr = r+dir, nc = c+dc;
      if (inBounds(nr,nc)) {
        if (colorOf(board[nr][nc]) === opp) moves.push([nr,nc]);
        if (enPassant && enPassant[0] === nr && enPassant[1] === nc) moves.push([nr,nc]);
      }
    });
  }

  return moves;
}

function findKing(board, col) {
  for (let r = 0; r < 8; r++)
    for (let c = 0; c < 8; c++)
      if (board[r][c] === col + "K") return [r, c];
  return null;
}

function isAttacked(board, r, c, byColor) {
  for (let fr = 0; fr < 8; fr++)
    for (let fc = 0; fc < 8; fc++)
      if (colorOf(board[fr][fc]) === byColor)
        if (rawMoves(board, fr, fc, null).some(([mr,mc]) => mr===r && mc===c))
          return true;
  return false;
}

function applyMove(board, from, to, enPassant, promotion) {
  const nb = board.map(r => [...r]);
  const piece = nb[from[0]][from[1]];
  const col = colorOf(piece);
  const type = typeOf(piece);

  // En passant capture
  if (type === "P" && enPassant && to[0] === enPassant[0] && to[1] === enPassant[1]) {
    const captureRow = col === "w" ? to[0]+1 : to[0]-1;
    nb[captureRow][to[1]] = null;
  }

  nb[to[0]][to[1]] = piece;
  nb[from[0]][from[1]] = null;

  // Promotion
  if (type === "P" && (to[0] === 0 || to[0] === 7)) {
    nb[to[0]][to[1]] = col + (promotion || "Q");
  }

  return nb;
}

function legalMoves(board, r, c, enPassant, castling) {
  const piece = board[r][c];
  if (!piece) return [];
  const col = colorOf(piece);
  const opp = col === "w" ? "b" : "w";
  let moves = rawMoves(board, r, c, enPassant);

  // Castling
  if (typeOf(piece) === "K") {
    const row = col === "w" ? 7 : 0;
    if (r === row && c === 4) {
      // Kingside
      if (castling[col+"K"] &&
          !board[row][5] && !board[row][6] &&
          !isAttacked(board, row, 4, opp) &&
          !isAttacked(board, row, 5, opp) &&
          !isAttacked(board, row, 6, opp))
        moves.push([row, 6, "castleK"]);
      // Queenside
      if (castling[col+"Q"] &&
          !board[row][3] && !board[row][2] && !board[row][1] &&
          !isAttacked(board, row, 4, opp) &&
          !isAttacked(board, row, 3, opp) &&
          !isAttacked(board, row, 2, opp))
        moves.push([row, 2, "castleQ"]);
    }
  }

  // Filter moves that leave king in check
  moves = moves.filter(([nr, nc]) => {
    let nb = applyMove(board, [r,c], [nr,nc], enPassant, null);
    // Handle castling rook
    if (typeOf(piece) === "K" && Math.abs(nc - c) === 2) {
      if (nc === 6) { nb[r][5] = col+"R"; nb[r][7] = null; }
      if (nc === 2) { nb[r][3] = col+"R"; nb[r][0] = null; }
    }
    const [kr, kc] = findKing(nb, col);
    return !isAttacked(nb, kr, kc, opp);
  });

  return moves;
}

function newCastling(castling, piece, from) {
  const c = { ...castling };
  const col = colorOf(piece);
  const type = typeOf(piece);
  if (type === "K") { c[col+"K"] = false; c[col+"Q"] = false; }
  if (type === "R") {
    const row = col === "w" ? 7 : 0;
    if (from[0] === row && from[1] === 7) c[col+"K"] = false;
    if (from[0] === row && from[1] === 0) c[col+"Q"] = false;
  }
  return c;
}

function newEnPassant(piece, from, to) {
  if (typeOf(piece) === "P" && Math.abs(to[0] - from[0]) === 2) {
    return [(from[0]+to[0])/2, from[1]];
  }
  return null;
}

function isInCheck(board, col) {
  const opp = col === "w" ? "b" : "w";
  const [kr, kc] = findKing(board, col);
  return isAttacked(board, kr, kc, opp);
}

function hasAnyMove(board, col, enPassant, castling) {
  for (let r = 0; r < 8; r++)
    for (let c = 0; c < 8; c++)
      if (colorOf(board[r][c]) === col)
        if (legalMoves(board, r, c, enPassant, castling).length > 0)
          return true;
  return false;
}

function gameStatus(board, currentTurn, enPassant, castling) {
  const hasMove = hasAnyMove(board, currentTurn, enPassant, castling);
  const inCheck = isInCheck(board, currentTurn);
  if (!hasMove && inCheck)  return "checkmate";
  if (!hasMove && !inCheck) return "stalemate";
  if (inCheck)              return "check";
  return "playing";
}

const INIT_CASTLING = { wK: true, wQ: true, bK: true, bQ: true };

function boardToStr(board) { return JSON.stringify(board); }
function strToBoard(s)     { return JSON.parse(s); }

function initGameState() {
  return {
    board:       boardToStr(INIT_BOARD),
    currentTurn: "w",
    enPassant:   null,
    castling:    INIT_CASTLING,
    status:      "playing",
    lastMove:    null,
    players:     {},
  };
}

// ─── COMPONENTI UI ────────────────────────────────────────────────────────────
const ExpiredScreen = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, padding:"2rem", fontFamily:FM }}>
    <div style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, borderRadius:24, padding:"2.5rem 2rem", maxWidth:340, width:"100%", textAlign:"center" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"1rem" }}>⏱</div>
      <h2 style={{ margin:"0 0 0.5rem", fontSize:"1.1rem", fontWeight:700, color:C.text }}>Sessione scaduta</h2>
      <p style={{ margin:0, fontSize:"0.85rem", color:C.sec, lineHeight:1.6 }}>Scansiona il QR del tavolo per iniziare una nuova sessione.</p>
    </div>
  </div>
);

const Spinner = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FM }}>
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem" }}>
      <div style={{ width:32, height:32, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.sec}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <p style={{ margin:0, fontSize:"0.82rem", color:C.sec }}>Connessione…</p>
    </div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ─── COMPONENTE PRINCIPALE ────────────────────────────────────────────────────
export default function ScacchiGame() {
  const { localeTag, tableId } = useParams();
  const navigate = useNavigate();
  const tokenUrl = new URLSearchParams(useLocation().search).get("token");
  const normalizedLocaleTag = (localeTag || "").toLowerCase();
  const roomId    = useMemo(() => `scacchi_${localeTag}_${tableId}_${tokenUrl || "notoken"}`, [localeTag, tableId, tokenUrl]);
  const playerKey = useMemo(() => `scacchiPlayer_${localeTag}_${tableId}`, [localeTag, tableId]);

  const [game, setGame]               = useState(null);
  const [role, setRole]               = useState("loading");
  const [error, setError]             = useState("");
  const [tokenScaduto, setTS]         = useState(false);
  const [tokenLoading, setTL]         = useState(true);
  const [selected, setSelected]       = useState(null);    // [r,c]
  const [highlights, setHighlights]   = useState([]);      // [[r,c],...]
  const [promotionPending, setPromo]  = useState(null);    // {from, to}

  // ── Verifica token ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        if (!tokenUrl) { setTS(true); setTL(false); return; }
        const snap = await getDoc(doc(db, "locali", normalizedLocaleTag, "tavoli", tableId));
        if (!snap.exists()) { setTS(true); setTL(false); return; }
        const { tokenAttuale } = snap.data() || {};
        if (!tokenAttuale || tokenUrl !== tokenAttuale) setTS(true);
        setTL(false);
      } catch { setTS(true); setTL(false); }
    };
    check();
  }, [tokenUrl, normalizedLocaleTag, tableId]);

  // ── Join / listen ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (tokenScaduto) return;
    let unsub;
    const join = async () => {
      try {
        let pid = localStorage.getItem(playerKey);
        if (!pid) { pid = Math.random().toString(36).slice(2); localStorage.setItem(playerKey, pid); }
        const rRef = doc(db, "scacchi", roomId);
        const snap = await getDoc(rRef);
        if (!snap.exists()) {
          const init = { ...initGameState(), players: { w: pid }, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
          await setDoc(rRef, init);
          setRole("w");
        } else {
          const { players = {} } = snap.data() || {};
          if (players.w === pid)       setRole("w");
          else if (players.b === pid)  setRole("b");
          else if (!players.w)         { await updateDoc(rRef, { "players.w": pid, updatedAt: serverTimestamp() }); setRole("w"); }
          else if (!players.b)         { await updateDoc(rRef, { "players.b": pid, updatedAt: serverTimestamp() }); setRole("b"); }
          else                         setRole("spectator");
        }
        unsub = onSnapshot(rRef, d => {
          if (!d.exists()) return;
          setGame(d.data());
        });
      } catch (e) { setError("Errore connessione."); setRole("spectator"); }
    };
    join();
    return () => { if (unsub) unsub(); };
  }, [roomId, playerKey, tokenScaduto]);

  // ── Selezione e mossa ───────────────────────────────────────────────────────
  const handleSquare = useCallback(async (r, c) => {
    if (!game || role === "loading" || role === "spectator") return;
    const board = strToBoard(game.board);
    const status = game.status;
    if (status === "checkmate" || status === "stalemate") return;
    if (game.currentTurn !== role) return;

    const piece = board[r][c];

    // Se ho già una pedina selezionata e clicco su una mossa valida
    if (selected) {
      const isHighlighted = highlights.some(([hr,hc]) => hr===r && hc===c);
      if (isHighlighted) {
        // Promozione pedina?
        const movingPiece = board[selected[0]][selected[1]];
        const isPromotion = typeOf(movingPiece) === "P" && (r === 0 || r === 7);
        if (isPromotion) {
          setPromo({ from: selected, to: [r,c] });
          setSelected(null);
          setHighlights([]);
          return;
        }
        await executeMove(selected, [r, c], null);
        return;
      }
      // Clicco su un'altra mia pedina → cambio selezione
      if (piece && colorOf(piece) === role) {
        selectPiece(board, r, c);
        return;
      }
      // Clicco altrove → deseleziono
      setSelected(null);
      setHighlights([]);
      return;
    }

    // Nessuna selezione: seleziona pezzo del proprio colore
    if (piece && colorOf(piece) === role) {
      selectPiece(board, r, c);
    }
  }, [game, role, selected, highlights]);

  const selectPiece = (board, r, c) => {
    const moves = legalMoves(board, r, c, game.enPassant, game.castling);
    setSelected([r, c]);
    setHighlights(moves.map(([mr,mc]) => [mr,mc]));
  };

  const executeMove = async (from, to, promotion) => {
    try {
      const board = strToBoard(game.board);
      const piece = board[from[0]][from[1]];
      const col = colorOf(piece);
      const opp = col === "w" ? "b" : "w";

      let nb = applyMove(board, from, to, game.enPassant, promotion);

// Gestisci arrocco solo se il Re si muove davvero di 2 caselle
if (typeOf(piece) === "K" && from[1] === 4 && Math.abs(to[1] - from[1]) === 2) {
  if (to[1] === 6) {
    nb[from[0]][5] = col + "R";
    nb[from[0]][7] = null;
  }

  if (to[1] === 2) {
    nb[from[0]][3] = col + "R";
    nb[from[0]][0] = null;
  }
}

      const newCast  = newCastling(game.castling, piece, from);
      const newEP    = newEnPassant(piece, from, to);
      const status   = gameStatus(nb, opp, newEP, newCast);

      await updateDoc(doc(db, "scacchi", roomId), {
        board:       boardToStr(nb),
        currentTurn: opp,
        enPassant:   newEP,
        castling:    newCast,
        status,
        lastMove:    { from, to },
        updatedAt:   serverTimestamp(),
      });

      setSelected(null);
      setHighlights([]);
      setPromo(null);
    } catch { setError("Errore mossa."); }
  };

  const restart = async () => {
    if (role === "loading" || role === "spectator") return;
    try {
      await updateDoc(doc(db, "scacchi", roomId), {
        ...initGameState(),
        players: game.players,
        updatedAt: serverTimestamp(),
      });
      setSelected(null);
      setHighlights([]);
      setPromo(null);
      setError("");
    } catch { setError("Errore riavvio."); }
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────
  if (tokenLoading) return <Spinner />;
  if (tokenScaduto) return <ExpiredScreen />;
  if (!game) return <Spinner />;

  const board       = strToBoard(game.board);
  const status      = game.status;
  const currentTurn = game.currentTurn;
  const lastMove    = game.lastMove;
  const isOver      = status === "checkmate" || status === "stalemate";

  // Il bianco vede la scacchiera normale, il nero la vede ruotata
  const flipped = role === "b";

  const rows = flipped ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0];
  const cols = flipped ? [7,6,5,4,3,2,1,0] : [0,1,2,3,4,5,6,7];

  const statusText = () => {
    if (status === "checkmate") {
      const winner = currentTurn === "w" ? "Nero" : "Bianco";
      return `Scacco matto! Vince il ${winner}`;
    }
    if (status === "stalemate") return "Stallo – Patta!";
    if (status === "check")     return `Scacco al Re ${currentTurn === "w" ? "Bianco" : "Nero"}!`;
    const whoTurn = currentTurn === "w" ? "Bianco" : "Nero";
    return `Turno del ${whoTurn}`;
  };

  const myTurn = !isOver && currentTurn === role;

  return (
    <div
  style={{
    minHeight:"100vh",
    background:C.bg,
    display:"flex",
    flexDirection:"column",
    alignItems:"center",
    justifyContent:"flex-start",
    padding:"0",
    fontFamily:FM,
    color:C.text,

    touchAction:"manipulation",
    WebkitTapHighlightColor:"transparent",
  }}
>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.5 } }
        @keyframes popIn { 0% { transform:scale(0.85); opacity:0 } 100% { transform:scale(1); opacity:1 } }
        @keyframes shimmer { 0% { box-shadow: 0 0 0 0 rgba(201,168,76,0.4) } 70% { box-shadow: 0 0 0 8px rgba(201,168,76,0) } 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0) } }
        .sq:active { transform: scale(0.94); }
        .sq { transition: background 0.13s, transform 0.08s; }
        .piece-span { transition: transform 0.12s; display:block; }
        .sq:hover .piece-span { transform: scale(1.08); }
      `}</style>

      {/* ── HEADER ── */}
      <div
  style={{
    width:"100%",
    maxWidth: role === "spectator" ? 520 : "100vw",

    padding:"0.75rem 0.65rem 0",

    boxSizing:"border-box",
  }}
>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.85rem" }}>
          <div>
            <p style={{ margin:0, fontSize:"0.68rem", color:C.ter, fontWeight:500, letterSpacing:"0.08em", textTransform:"uppercase" }}>Giochi del tavolo</p>
            <h1 style={{ margin:"0.1rem 0 0", fontSize:"1.6rem", fontWeight:700, letterSpacing:"-0.5px", fontFamily:F }}>♟ Scacchi</h1>
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={{ margin:0, fontSize:"0.68rem", color:C.ter }}>Tavolo <strong style={{color:C.sec}}>{tableId}</strong></p>
            <p style={{ margin:0, fontSize:"0.68rem", color:C.ter }}>{localeTag}</p>
          </div>
        </div>

        {/* Player cards */}
        <div style={{ display:"flex", gap:"0.5rem", marginBottom:"0.7rem" }}>
          {[["w","Bianco","♔",C.gold,C.goldBg,C.goldBr], ["b","Nero","♚",C.silver,C.silverBg,C.silverBr]].map(([col, label, icon, accent, bg, br]) => (
            <div key={col} style={{
              flex:1, padding:"0.42rem 0.65rem",
              background: currentTurn===col && !isOver ? bg : "rgba(255,255,255,0.02)",
              border:`1px solid ${currentTurn===col && !isOver ? br : C.border}`,
              borderRadius:14,
              display:"flex", alignItems:"center", gap:"0.5rem",
              transition:"all 0.25s",
              animation: currentTurn===col && !isOver ? "shimmer 1.8s infinite" : "none",
            }}>
              <span style={{ fontSize:"1.3rem", lineHeight:1 }}>{icon}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ margin:0, fontSize:"0.75rem", fontWeight:700, color: currentTurn===col && !isOver ? accent : C.sec }}>{label}</p>
                {role===col && <p style={{ margin:0, fontSize:"0.62rem", color:C.ter }}>Tu</p>}
                {role!==col && role!=="spectator" && <p style={{ margin:0, fontSize:"0.62rem", color:C.ter }}>Avversario</p>}
              </div>
              {currentTurn===col && !isOver && (
                <div style={{ width:8, height:8, borderRadius:"50%", background:accent, animation:"pulse 1.2s infinite" }} />
              )}
            </div>
          ))}
        </div>

        {/* Status bar */}
        <div style={{
          padding:"0.42rem 0.8rem",
          background: isOver ? "rgba(201,168,76,0.08)" : status==="check" ? "rgba(220,60,60,0.10)" : "rgba(255,255,255,0.03)",
          border:`1px solid ${isOver ? C.goldBr : status==="check" ? "rgba(220,60,60,0.35)" : C.border}`,
          borderRadius:10, marginBottom:"0.7rem", textAlign:"center",
          animation:"fadeIn 0.3s ease",
        }}>
          <p style={{ margin:0, fontSize:"0.85rem", fontWeight:700, color: isOver?C.gold : status==="check"?"#ff6b6b" : myTurn?C.text:C.sec }}>
            {statusText()}
          </p>
          {!isOver && role!=="spectator" && !myTurn &&
            <p style={{ margin:"2px 0 0", fontSize:"0.68rem", color:C.ter }}>Aspetta il tuo turno…</p>}
          {role==="spectator" &&
            <p style={{ margin:"2px 0 0", fontSize:"0.68rem", color:C.ter }}>Modalità spettatore</p>}
        </div>

        {error && <p style={{ margin:"0 0 0.5rem", fontSize:"0.78rem", color:"#ff453a", textAlign:"center" }}>{error}</p>}
      </div>

      {/* ── SCACCHIERA FULL WIDTH ── */}
      <div style={{ width:"100%", maxWidth:"100vw", padding:"0", boxSizing:"border-box", marginBottom:"0.75rem" }}>

        {/* Lettere colonne */}
        <div style={{ display:"flex", marginLeft:14, marginBottom:2 }}>
          {cols.map(c => (
            <div key={c} style={{ flex:1, textAlign:"center", fontSize:"0.62rem", color:C.ter, fontWeight:600 }}>
              {String.fromCharCode(97+c)}
            </div>
          ))}
        </div>

        <div style={{ display:"flex" }}>
          {/* Numeri righe */}
          <div style={{ display:"flex", flexDirection:"column", width:14 }}>
            {rows.map(r => (
              <div key={r} style={{ flex:1, aspectRatio:"1", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.62rem", color:C.ter, fontWeight:600 }}>
                {r+1}
              </div>
            ))}
          </div>

          {/* Griglia */}
          <div style={{
            flex:1,
            display:"grid",
            gridTemplateColumns:"repeat(8,1fr)",
            borderRadius:14,
            overflow:"hidden",
            boxShadow:"0 8px 40px rgba(0,0,0,0.6), 0 0 0 2px rgba(255,255,255,0.06)",
          }}>
            {rows.map(r =>
              cols.map(c => {
                const isLight     = (r+c) % 2 === 0;
                const piece       = board[r][c];
                const isSel       = selected && selected[0]===r && selected[1]===c;
                const isHL        = highlights.some(([hr,hc]) => hr===r && hc===c);
                const isLastFrom  = lastMove && lastMove.from[0]===r && lastMove.from[1]===c;
                const isLastTo    = lastMove && lastMove.to[0]===r && lastMove.to[1]===c;
                const isLast      = isLastFrom || isLastTo;
                const isCapture   = isHL && !!piece;
                const isKingCheck = piece === currentTurn+"K" && status === "check";

                let bg = isLight ? "#d4c5a9" : "#5c4a32";
                if (isLast)       bg = isLight ? "#e2cf80" : "#a07d30";
                if (isSel)        bg = isLight ? "#c9a84c" : "#9a7020";
                if (isKingCheck)  bg = isLight ? "#e87070" : "#b04040";

                return (
                  <div
                    key={`${r}-${c}`}
                    className="sq"
                    onClick={() => handleSquare(r, c)}
                    style={{
                      aspectRatio:"1",
                      background: bg,
                      position:"relative",
                      cursor: myTurn ? "pointer" : "default",
touchAction:"manipulation",
display:"flex",
                      alignItems:"center",
                      justifyContent:"center",
                      userSelect:"none",
                    }}
                  >
                    {/* Dot mossa possibile */}
                    {isHL && !isCapture && (
                      <div style={{
                        width:"32%", height:"32%", borderRadius:"50%",
                        background:"rgba(0,0,0,0.22)",
                        pointerEvents:"none",
                        animation:"popIn 0.15s ease",
                      }} />
                    )}
                    {/* Ring cattura */}
                    {isCapture && (
                      <div style={{
                        position:"absolute", inset:0,
                        borderRadius:"inherit",
                        background:"radial-gradient(circle, transparent 55%, rgba(180,40,40,0.55) 55%)",
                        pointerEvents:"none",
                        animation:"popIn 0.15s ease",
                      }} />
                    )}
                    {/* Freccia "mossa avversario" sulla casella di arrivo */}
                    {isLastTo && !isSel && (
                      <div style={{
                        position:"absolute", top:3, right:3,
                        width:7, height:7, borderRadius:"50%",
                        background: currentTurn==="w" ? C.silver : C.gold,
                        opacity:0.85,
                        pointerEvents:"none",
                      }} />
                    )}
                    {/* Pezzo */}
                    {piece && (
                      <span
                        className="piece-span"
                        style={{
                          fontSize:"clamp(2.2rem, 11.8vw, 3.6rem)",
                          lineHeight:1,
                          color: colorOf(piece)==="w"
  ? "#ffffff"
  : "#121212",

textShadow: colorOf(piece)==="w"
  ? `
      0 0 1px rgba(0,0,0,1),
      0 1px 2px rgba(0,0,0,0.95),
      0 0 3px rgba(0,0,0,0.9)
    `
  : `
      0 1px 1px rgba(255,255,255,0.10)
    `,
                          fontFamily:F,
                          zIndex:1,
                          position:"relative",
                          animation: isLastTo ? "popIn 0.2s ease" : "none",
                        }}
                      >
                        {PIECE_UNICODE[piece]}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
        </div>
        <div
  style={{
    width:"100%",
    maxWidth:520,
    padding:"0 0.6rem",
    boxSizing:"border-box",
    marginBottom:"0.8rem",
  }}
>
  <div
    style={{
      display:"flex",
      alignItems:"center",
      justifyContent:"center",
      gap:"0.5rem",

      padding:"0.7rem 1rem",

      borderRadius:14,

      background:
        role === "w"
          ? "rgba(201,168,76,0.10)"
          : "rgba(138,155,176,0.10)",

      border:
        role === "w"
          ? `1px solid ${C.goldBr}`
          : `1px solid ${C.silverBr}`,
    }}
  >
    <div
      style={{
        width:14,
        height:14,
        borderRadius:"50%",
        background:
          role === "w"
            ? "#fffdf7"
            : "#161616",

        border:"1px solid rgba(255,255,255,0.15)",
      }}
    />

    <p
      style={{
        margin:0,
        fontSize:"0.82rem",
        fontWeight:700,
        color:C.text,
      }}
    >
      Stai giocando con i pezzi {role === "w" ? "Bianchi" : "Neri"}
    </p>
  </div>
</div>
      </div>

      {/* ── LEGENDA ULTIMA MOSSA ── */}
      {lastMove && (
        <div style={{ width:"100%", maxWidth:520, padding:"0 0.5rem", boxSizing:"border-box", marginBottom:"0.6rem" }}>
          <p style={{ margin:0, fontSize:"0.7rem", color:C.ter, textAlign:"center" }}>
            Ultima mossa:{" "}
            <span style={{ color: currentTurn==="w" ? C.silver : C.gold, fontWeight:600 }}>
              {String.fromCharCode(97+lastMove.from[1])}{lastMove.from[0]+1}
              {" → "}
              {String.fromCharCode(97+lastMove.to[1])}{lastMove.to[0]+1}
            </span>
          </p>
        </div>
      )}

      {/* ── PROMOZIONE ── */}
      {promotionPending && (
        <div style={{
          width:"100%", maxWidth:520, padding:"0 0.5rem", boxSizing:"border-box", marginBottom:"0.7rem",
          animation:"fadeIn 0.2s ease",
        }}>
          <div style={{ padding:"1rem", background:"rgba(201,168,76,0.08)", border:`1px solid ${C.goldBr}`, borderRadius:14, textAlign:"center" }}>
            <p style={{ margin:"0 0 0.7rem", fontSize:"0.85rem", color:C.gold, fontWeight:700 }}>Promuovi la pedina:</p>
            <div style={{ display:"flex", justifyContent:"center", gap:"0.6rem" }}>
              {["Q","R","B","N"].map(t => (
                <button key={t} onClick={() => executeMove(promotionPending.from, promotionPending.to, t)}
                  style={{
                    padding:"0.6rem 0.9rem", background:C.goldBg, border:`1px solid ${C.goldBr}`,
                    borderRadius:12, color:C.text, fontSize:"1.6rem", cursor:"pointer", fontFamily:F,
                    transition:"background 0.15s, transform 0.1s",
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.background="rgba(201,168,76,0.25)";e.currentTarget.style.transform="scale(1.1)"}}
                  onMouseLeave={e=>{e.currentTarget.style.background=C.goldBg;e.currentTarget.style.transform="scale(1)"}}>
                  {PIECE_UNICODE[role+t]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── BOTTONI ── */}
      <div style={{ width:"100%", maxWidth:520, padding:"0 0.5rem 1.5rem", boxSizing:"border-box", display:"flex", flexDirection:"column", gap:"0.45rem" }}>
        <button onClick={restart}
          style={{ width:"100%", padding:"0.8rem", background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:13, color:C.text, fontSize:"0.88rem", fontWeight:600, cursor:"pointer", fontFamily:FM, transition:"background 0.15s" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.sh}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
          ↺ Nuova partita
        </button>
        <button onClick={() => navigate(`/giochi/${localeTag}/${tableId}?token=${tokenUrl}`)}
          style={{ width:"100%", padding:"0.75rem", background:"transparent", border:`1px solid ${C.border}`, borderRadius:13, color:C.sec, fontSize:"0.84rem", fontWeight:500, cursor:"pointer", fontFamily:FM, transition:"background 0.15s" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.surface}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          ← Torna ai giochi
        </button>
      </div>

    </div>
  );
}
